import React, { useState, useEffect, useCallback } from "react";
import ProgressBar from "react-bootstrap/ProgressBar";
import { Card, Row, Col, Container, Button, Table, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import CustomProgressBar from "views/Components/CustomProgressBar ";
import axios from "axios";
import { API_BASE_URL } from "config.js";
import Swal from "sweetalert2";

function Campaigns() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);
  const [inspectedCampaignIndex, setInspectedCampaignIndex] = useState(0);
  const [optOutsCount, setOptOutsCount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const storedUser = localStorage.getItem("user");
  const currentUser = storedUser ? JSON.parse(storedUser) : null;
  const isAdmin = currentUser && (currentUser.type === "admin" || currentUser.type === "super admin");

  const [pendingCampaigns, setPendingCampaigns] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  const [downloadingRepliesId, setDownloadingRepliesId] = useState(null);

  const handleDownloadReplies = async (campaignId, campaignName) => {
    try {
      setDownloadingRepliesId(campaignId);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/campaigns/${campaignId}/export-replies`, {
        headers,
        responseType: "blob"
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const safeName = (campaignName || "campaign").replace(/[^a-z0-9]/gi, "_").toLowerCase();
      link.setAttribute("download", `replies_${safeName}_${campaignId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading campaign replies:", err);
      Swal.fire("Error", "Failed to export campaign replies. Please try again.", "error");
    } finally {
      setDownloadingRepliesId(null);
    }
  };

  const handleDownloadReport = async (campaignId, campaignName) => {
    try {
      setIsDownloading(true);
      setIsProcessing(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/campaigns/${campaignId}/export`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = campaignName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
      link.setAttribute('download', `report_${safeName}_${campaignId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error downloading report", err);
      alert("Failed to download report. Please try again.");
    } finally {
      setIsDownloading(false);
      setIsProcessing(false);
    }
  };

  const handleUnassignNumber = async (campaign, phoneNumberId) => {
    if (campaign.status === "processing") {
      Swal.fire({
        icon: "warning",
        title: "Cannot Unassign",
        text: "Cannot unassign a phone number from an active campaign while it is sending.",
        confirmButtonColor: "#004992"
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: "Do you want to unassign this phone number from this campaign?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#004992",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, unassign it"
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.post(`${API_BASE_URL}/campaigns/${campaign._id}/unassign-number`, { phoneNumberId }, { headers });
        if (res.data.success) {
          Swal.fire("Unassigned!", "The phone number has been unassigned.", "success");
          fetchCampaigns();
        }
      } catch (err) {
        console.error("Error unassigning number:", err);
        Swal.fire("Error", err.response?.data?.message || "Failed to unassign phone number.", "error");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const fetchPendingCampaigns = useCallback(async () => {
    if (!isAdmin) return;
    try {
      setLoadingPending(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/campaigns/admin/pending`, { headers });
      if (response.data.success) {
        setPendingCampaigns(response.data.campaigns || []);
      }
    } catch (err) {
      console.error("Error fetching pending campaigns:", err);
    } finally {
      setLoadingPending(false);
    }
  }, [isAdmin]);

  const handleApproveCampaign = async (campaignId) => {
    setIsProcessing(true);
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.post(`${API_BASE_URL}/campaigns/admin/approve/${campaignId}`, {}, { headers });
      if (res.data.success) {
        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: "The campaign test message has been approved.",
          timer: 2000,
          showConfirmButton: false
        });
        fetchPendingCampaigns();
        fetchCampaigns();
      }
    } catch (err) {
      console.error("Error approving campaign:", err);
      Swal.fire("Error", err.response?.data?.message || "Failed to approve campaign.", "error");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDenyCampaign = async (campaignId) => {
    const result = await Swal.fire({
      title: "Deny Campaign?",
      text: "Are you sure you want to deny this campaign's test message?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, deny it"
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.post(`${API_BASE_URL}/campaigns/admin/deny/${campaignId}`, {}, { headers });
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Denied",
            text: "The campaign test message has been denied.",
            timer: 2000,
            showConfirmButton: false
          });
          fetchPendingCampaigns();
          fetchCampaigns();
        }
      } catch (err) {
        console.error("Error denying campaign:", err);
        Swal.fire("Error", err.response?.data?.message || "Failed to deny campaign.", "error");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleDeleteCampaign = async (campaignId) => {
    const result = await Swal.fire({
      title: "Delete Draft?",
      text: "Are you sure you want to permanently delete this campaign draft?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#aaa",
      confirmButtonText: "Yes, delete it"
    });

    if (result.isConfirmed) {
      setIsProcessing(true);
      try {
        const token = localStorage.getItem("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.delete(`${API_BASE_URL}/campaigns/${campaignId}`, { headers });
        if (res.data.success) {
          Swal.fire({
            icon: "success",
            title: "Deleted",
            text: "Campaign draft has been deleted.",
            timer: 2000,
            showConfirmButton: false
          });
          fetchCampaigns();
        }
      } catch (err) {
        console.error("Error deleting campaign draft:", err);
        Swal.fire("Error", err.response?.data?.message || "Failed to delete campaign draft.", "error");
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const fetchCampaigns = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API_BASE_URL}/campaigns`, { headers });
      if (response.data.success) {
        setCampaigns(response.data.campaigns || []);
        setOptOutsCount(response.data.optOutsCount || 0);
      }
    } catch (err) {
      console.error("Error fetching campaigns", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
    fetchPendingCampaigns();
  }, [fetchCampaigns, fetchPendingCampaigns]);

  useEffect(() => {
    const hasActiveAutomated = campaigns.some(
      (c) => c.campaignType !== "p2p" && (c.status === "processing" || c.status === "sending")
    );
    const intervalTime = hasActiveAutomated ? 5000 : 30000;
    const interval = setInterval(() => {
      fetchCampaigns();
      fetchPendingCampaigns();
    }, intervalTime);
    return () => clearInterval(interval);
  }, [campaigns, fetchCampaigns, fetchPendingCampaigns]);

  const totalAttempted = campaigns.reduce((acc, c) => acc + (c.stats?.sent || 0) + (c.stats?.delivered || 0) + (c.stats?.failed || 0) + (c.stats?.bounced || 0), 0);
  const totalSuccess = campaigns.reduce((acc, c) => acc + (c.stats?.sent || 0) + (c.stats?.delivered || 0), 0);
  const optOuts = optOutsCount;
  const deliveryRate = totalAttempted > 0 ? ((totalSuccess / totalAttempted) * 100).toFixed(1) + "%" : "0%";

  const stats = [
    { title: "Messages Sent", value: totalSuccess.toLocaleString(), subtext: "All Time", icon: "far fa-comment-alt" },
    { title: "Delivery Rate", value: deliveryRate, subtext: "Average", icon: "fas fa-paper-plane", textColor: "#009222" },
    { title: "Total Reach", value: totalSuccess.toLocaleString(), subtext: "All campaigns", icon: "fas fa-users" },
    { title: "Opt-outs", value: optOuts, subtext: "Total", icon: "fas fa-user-minus", textColor: "text-danger", iconColor: "text-danger" },
  ];

  const activeCampaign = campaigns.length > 0 && inspectedCampaignIndex < campaigns.length ? campaigns[inspectedCampaignIndex] : campaigns.length > 0 ? campaigns[0] : null;

  const getStatusColor = (status) => {
    switch (status) {
      case "completed": return { text: "Completed", bg: "#BDFFCC", color: "#009222" };
      case "processing": return { text: "Processing", bg: "#BFDFFF", color: "#004992" };
      case "failed": return { text: "Failed", bg: "#FFDCDC", color: "#D10000" };
      default: return { text: "Draft", bg: "#E0E0E0", color: "#5E5E5E" };
    }
  };
  return (
    <Container fluid className="py-0 px-5" style={{ position: "relative" }}>
      {isProcessing && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Spinner animation="border" variant="primary" style={{ width: "3.5rem", height: "3.5rem" }} />
          <h4 className="mt-3 text-dark fw-bold">Processing Request, Please Wait...</h4>
        </div>
      )}
      {/* Header */}
      <Row className="mb-4">
        <Col className="d-flex justify-content-between align-items-center">
          <div>
            <h2 className="fw-bold mb-1" style={{ fontWeight: 700 }}>Campaigns</h2>
            <p className="text-muted mb-0" style={{ fontWeight: 600 }}>
              Manage and monitor your messaging campaigns
            </p>
          </div>
          <Button
            className="px-4 mt-4 fw-bold"
            style={{
              backgroundColor: "#004992",
              borderColor: "#004992",
              borderRadius: "6px",
              padding: "10px 24px",
              width: '300px'
            }}
            onClick={() => navigate("/admin/create-campaign")}
          >
            Create Campaign
          </Button>
        </Col>
      </Row>

      {/* Stats Cards */}
      <Row className="mb-3 g-4">
        {stats.map((stat, index) => (
          <Col key={index} lg={3} md={6}>
            <Card style={{ border: "1px solid #c7c7c7", borderRadius: '8px' }} className="h-100 shadow-sm px-3">
              <Card.Body className="d-flex justify-content-between align-items-start">
                <div>
                  <h6 style={{ fontWeight: "700" }} className="text-muted mb-1">{stat.title}</h6>
                  <h2 className="fw-bold my-3" style={{ fontWeight: 700 }}>{stat.value}</h2>
                  <p style={{ fontWeight: "700", color: `${stat.textColor}` }} className={`fw-bold mb-0 ${stat.textColor || "text-muted"}`}>
                    {stat.subtext}
                  </p>
                </div>
                <i
                  className={`${stat.icon} ${stat.iconColor || "text-dark"}`}
                  style={{ fontSize: "1.5rem" }}
                ></i>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Admin Pending approvals */}
      {isAdmin && pendingCampaigns.length > 0 && (
        <Row className="mb-4">
          <Col>
            <Card className="shadow-sm border-0" style={{ border: "1px solid #c7c7c7", borderRadius: "8px" }}>
              <Card.Header className="bg-light border-0 py-3 d-flex justify-content-between align-items-center" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                <div>
                  <h4 className="fw-bold m-0 text-primary" style={{ fontWeight: 700 }}>
                    <i className="fas fa-clipboard-check me-2" style={{ marginRight: "8px" }}></i>
                    Pending Campaign Approvals
                  </h4>
                  <p className="text-muted mb-0 small" style={{ fontWeight: 600 }}>
                    Review and approve or deny test SMS contents for campaigns launched by customers under Force Test Message constraints.
                  </p>
                </div>
                <span className="badge bg-warning text-dark px-3 py-2 fw-bold" style={{ borderRadius: "20px" }}>
                  {pendingCampaigns.length} Action Needed
                </span>
              </Card.Header>
              <Card.Body className="p-0">
                <Table hover responsive className="mb-0">
                  <thead className="table-light">
                    <tr style={{ borderBottom: "2px solid #c7c7c7" }}>
                      <th className="px-4" style={{ fontWeight: "700" }}>Customer</th>
                      <th style={{ fontWeight: "700" }}>Campaign Name</th>
                      <th style={{ fontWeight: "700" }}>Message Template</th>
                      <th style={{ fontWeight: "700" }}>Target Audience</th>
                      <th style={{ fontWeight: "700" }}>Phone Numbers</th>
                      <th style={{ fontWeight: "700", textAlign: "right" }} className="px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pendingCampaigns.map((camp) => (
                      <tr key={camp._id} style={{ borderBottom: "1px solid #e9ecef" }}>
                        <td className="px-4 fw-bold" style={{ verticalAlign: "middle" }}>
                          {camp.userId?.name || "Unknown"}
                          <div className="small text-muted" style={{ fontWeight: 500 }}>{camp.userId?.email || ""}</div>
                        </td>
                        <td style={{ verticalAlign: "middle", fontWeight: 600 }}>{camp.name}</td>
                        <td style={{ verticalAlign: "middle", maxWidth: "250px", wordBreak: "break-all", fontWeight: 500 }} className="text-muted">
                          {camp.messageBody}
                          {camp.mediaUrl && (
                            <div className="mt-1">
                              <span className="badge bg-info text-white">MMS Media Included</span>
                            </div>
                          )}
                        </td>
                        <td style={{ verticalAlign: "middle", fontWeight: 600 }}>
                          {camp.contactListId?.listName || "N/A"} ({camp.contactListId?.countContact || 0} contacts)
                        </td>
                        <td style={{ verticalAlign: "middle", fontWeight: 600 }}>
                          {camp.phoneNumbers && camp.phoneNumbers.length > 0 ? (
                            <div className="d-flex flex-wrap gap-1">
                              {camp.phoneNumbers.map(n => (
                                <span key={n._id} className="badge bg-light text-dark border px-2 py-1" style={{ borderRadius: "12px", fontSize: "0.75rem", marginRight: "4px" }}>
                                  {n.phoneNumber}
                                </span>
                              ))}
                            </div>
                          ) : "N/A"}
                        </td>
                        <td style={{ verticalAlign: "middle", textAlign: "right" }} className="px-4">
                          <Button
                            variant="success"
                            size="sm"
                            className="fw-bold px-3 py-1 bg-success text-white border-0 me-2 mr-2"
                            style={{ borderRadius: "4px" }}
                            onClick={() => handleApproveCampaign(camp._id)}
                          >
                            Approve
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            className="fw-bold px-3 py-1 bg-danger text-white border-0"
                            style={{ borderRadius: "4px" }}
                            onClick={() => handleDenyCampaign(camp._id)}
                          >
                            Deny
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Active Campaign */}
      <Row className="mb-3">
        <Col className="d-flex justify-content-between align-items-center">
          <h3 className="fw-bold m-0" style={{ fontWeight: 700 }}>Active Campaign</h3>
          {activeCampaign && activeCampaign.status !== "draft" && (
            <div className="d-flex gap-2">
              <Button
                variant="outline-success"
                className="fw-bold px-3 py-2 me-2 mr-2"
                style={{
                  borderRadius: "6px",
                  fontSize: "0.95rem"
                }}
                onClick={() => handleDownloadReplies(activeCampaign._id, activeCampaign.name)}
                disabled={downloadingRepliesId === activeCampaign._id}
              >
                {downloadingRepliesId === activeCampaign._id ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Downloading...</>
                ) : (
                  <><i className="fas fa-reply me-2"></i>Download Replies</>
                )}
              </Button>
              <Button
                variant="outline-primary"
                className="fw-bold px-3 py-2"
                style={{
                  backgroundColor: "#004992",
                  color: "#fff",
                  borderColor: "#004992",
                  borderRadius: "6px",
                  fontSize: "0.95rem"
                }}
                onClick={() => handleDownloadReport(activeCampaign._id, activeCampaign.name)}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <><span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Downloading...</>
                ) : (
                  <><i className="fas fa-file-csv me-2"></i>Download Report</>
                )}
              </Button>
            </div>
          )}
        </Col>
      </Row>

      {isLoading ? (
        <Row><Col><p className="text-muted fw-bold">Loading campaign data...</p></Col></Row>
      ) : activeCampaign ? (
      <Row>
        <Col>
          <Card className="shadow-sm border-0">
            <Card.Body>
              <Row className="align-items-center mb-3">
                <Col md={9} className="d-flex">
                  <i className="far fa-comment-alt text-dark fs-3 mr-3 mt-1"></i>
                  <div>
                    <h5 className="fw-bold mb-1" style={{ fontWeight: 700 }}>{activeCampaign.name}</h5>
                    <p className="text-muted mb-0" style={{ fontWeight: 600 }}>
                      {activeCampaign.campaignType === "p2p" && (
                        <span className="badge bg-success text-white me-2" style={{ fontSize: "0.75rem", padding: "4px 8px" }}>P2P MANUAL</span>
                      )}
                      Started {new Date(activeCampaign.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </Col>
                <Col md={3} className="d-flex justify-content-end">
                  <span
                    className="badge px-3 py-2"
                    style={{ borderRadius: "6px", fontWeight: 600, background: getStatusColor(activeCampaign.status).bg, color: getStatusColor(activeCampaign.status).color }}
                  >
                    {getStatusColor(activeCampaign.status).text}
                  </span>
                </Col>
              </Row>

              <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="fw-bold text-muted mb-0" style={{ fontWeight: 700 }}>Progress</p>
                <span className="fw-bold text-muted" style={{ fontWeight: 700 }}>
                  {activeCampaign.stats?.totalContacts > 0 ? Math.min(Math.round(((activeCampaign.stats?.sent + activeCampaign.stats?.delivered + activeCampaign.stats?.bounced + activeCampaign.stats?.failed) / activeCampaign.stats?.totalContacts) * 100), 100) : 0}%
                </span>
              </div>
              <CustomProgressBar
                progress={activeCampaign.stats?.totalContacts > 0 ? Math.min(Math.round(((activeCampaign.stats?.sent + activeCampaign.stats?.delivered + activeCampaign.stats?.bounced + activeCampaign.stats?.failed) / activeCampaign.stats?.totalContacts) * 100), 100) : 0}
                height="16px"
                fillColor="#004992"
                backgroundColor="#BFDFFF"
                borderRadius="20px"
                className="w-100 mb-4"
              />

              {activeCampaign.campaignType === "p2p" && activeCampaign.status !== "completed" && activeCampaign.status !== "draft" && (
                <div className="mb-4">
                  <Button
                    className="fw-bold px-4 py-2 text-white w-100 border-0"
                    style={{
                      backgroundColor: "#009222",
                      borderRadius: "6px",
                      fontSize: "1rem"
                    }}
                    onClick={() => navigate(`/admin/campaigns/${activeCampaign._id}/p2p`)}
                  >
                    <i className="fas fa-paper-plane me-2"></i>Open Volunteer Send Console (Manual P2P)
                  </Button>
                </div>
              )}

              {/* Associated Phone Numbers */}
              <div className="mb-4">
                <p className="fw-bold text-muted mb-2" style={{ fontWeight: 700 }}>Associated Phone Numbers</p>
                {activeCampaign.phoneNumbers && activeCampaign.phoneNumbers.length > 0 ? (
                  <div className="d-flex flex-wrap gap-2">
                    {activeCampaign.phoneNumbers.map((num) => (
                      <span
                        key={num._id}
                        className="badge px-3 py-2 bg-light border text-dark d-flex align-items-center"
                        style={{ borderRadius: "20px", fontSize: "0.85rem", fontWeight: "600" }}
                      >
                        <i className="fas fa-phone-alt me-2 text-primary" style={{ marginRight: "6px" }}></i>
                        {num.phoneNumber} {num.friendlyName ? `(${num.friendlyName})` : ''}
                        <button
                          type="button"
                          className="btn-close ms-2 ml-2"
                          style={{
                            fontSize: "0.65rem",
                            cursor: "pointer",
                            background: "none",
                            border: "none",
                            color: "#dc3545",
                            fontWeight: "bold",
                            padding: 0,
                            marginLeft: "8px"
                          }}
                          onClick={() => handleUnassignNumber(activeCampaign, num._id)}
                          aria-label="Unassign Number"
                        >
                          ❌
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-muted small" style={{ fontWeight: "600" }}>
                    All phone numbers were unassigned from the campaign
                  </span>
                )}
              </div>

              <Row className="w-75">
                <Col>
                  <p className="fw-bold text-muted mb-1" style={{ fontWeight: 700 }}>Messages Queued</p>
                  <h5 className="fw-bold" style={{ fontWeight: 700 }}>{activeCampaign.stats?.totalContacts || 0}</h5>
                </Col>
                <Col>
                  <p className="fw-bold text-muted mb-1" style={{ fontWeight: 700 }}>Delivery Rate</p>
                  <h5 className="fw-bold" style={{ fontWeight: 700 }}>
                    {(() => {
                      const totalContacts = activeCampaign.stats?.totalContacts || 0;
                      const successCount = Math.min((activeCampaign.stats?.sent || 0) + (activeCampaign.stats?.delivered || 0), totalContacts);
                      const rate = totalContacts > 0 ? ((successCount / totalContacts) * 100).toFixed(1) + "%" : "0.0%";
                      return `${rate} (${successCount} of ${totalContacts})`;
                    })()}
                  </h5>
                </Col>
                <Col>
                  <p className="fw-bold text-muted mb-1" style={{ fontWeight: 700 }}>Bounced / Failed</p>
                  <h5 className="fw-bold" style={{ fontWeight: 700 }}>{(activeCampaign.stats?.bounced || 0) + (activeCampaign.stats?.failed || 0)}</h5>
                </Col>
              </Row>

              {/* Campaign History Table */}
              <Row className="mt-4">
                <Col>
                  <Card className="shadow-sm border-0" style={{ border: "1px solid #c7c7c7", borderRadius: "8px" }}>
                    <Card.Header className="bg-white border-0 py-3" style={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
                      <h4 className="fw-bold m-0" style={{ fontWeight: 700 }}>Campaign History</h4>
                      <p className="text-muted mb-0 small" style={{ fontWeight: 600 }}>Click on any campaign row to inspect its live status and download details above</p>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <Table hover responsive className="mb-0">
                        <thead className="table-light">
                          <tr style={{ borderBottom: "2px solid #c7c7c7" }}>
                            <th className="px-4" style={{ fontWeight: "700" }}>Campaign Name</th>
                            <th style={{ fontWeight: "700" }}>Date Created</th>
                            <th style={{ fontWeight: "700" }}>Status</th>
                            <th style={{ fontWeight: "700" }}>Contacts</th>
                            <th style={{ fontWeight: "700" }}>Delivery Rate</th>
                            <th style={{ fontWeight: "700" }}>Failed</th>
                            <th style={{ fontWeight: "700" }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {campaigns.map((camp, idx) => {
                            const statusColor = getStatusColor(camp.status);
                            const total = camp.stats?.totalContacts || 0;
                            const sent = camp.stats?.sent || 0;
                            const delivered = camp.stats?.delivered || 0;
                            const failed = (camp.stats?.bounced || 0) + (camp.stats?.failed || 0);
                            const successCount = Math.min(sent + delivered, total);
                            const rate = total > 0 ? ((successCount / total) * 100).toFixed(1) + "%" : "0.0%";
                            const isInspected = inspectedCampaignIndex === idx;

                            return (
                              <tr 
                                key={camp._id} 
                                style={{ 
                                  cursor: "pointer", 
                                  backgroundColor: isInspected ? "#f0f6ff" : "#fff",
                                  borderBottom: "1px solid #e9ecef"
                                }} 
                                onClick={() => setInspectedCampaignIndex(idx)}
                              >
                                <td className="px-4 fw-bold" style={{ color: isInspected ? "#004992" : "inherit" }}>
                                  {isInspected && <i className="fas fa-chevron-right me-2 text-primary"></i>} {camp.name}
                                </td>
                                <td style={{ fontWeight: 600 }}>{new Date(camp.createdAt).toLocaleDateString()}</td>
                                <td style={{ fontWeight: 600 }}>
                                  <span 
                                    className="badge px-2 py-1" 
                                    style={{ background: statusColor.bg, color: statusColor.color, borderRadius: "4px", fontWeight: 600 }}
                                  >
                                    {statusColor.text}
                                  </span>
                                </td>
                                <td style={{ fontWeight: 600 }}>{total}</td>
                                <td style={{ fontWeight: 600 }}>{rate} ({successCount} sent)</td>
                                <td style={{ fontWeight: 600 }} className={failed > 0 ? "text-danger" : ""}>{failed}</td>
                                <td>
                                  {camp.status === "draft" ? (
                                    <>
                                      <Button 
                                        size="sm" 
                                        className="fw-bold px-2 py-1 text-white border-0 me-2 mr-2" 
                                        style={{ backgroundColor: "#004992", borderRadius: "4px" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          navigate(`/admin/create-campaign?draftId=${camp._id}`);
                                        }}
                                      >
                                        <i className="fas fa-play me-1"></i> Resume
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        className="fw-bold px-2 py-1 text-white border-0 bg-danger" 
                                        style={{ borderRadius: "4px" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDeleteCampaign(camp._id);
                                        }}
                                      >
                                        <i className="fas fa-trash me-1"></i> Delete
                                      </Button>
                                    </>
                                  ) : (
                                    <div className="d-flex align-items-center">
                                      <Button 
                                        size="sm" 
                                        className="fw-bold px-2 py-1 text-white border-0 me-2 mr-2" 
                                        style={{ backgroundColor: "#004992", borderRadius: "4px" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadReport(camp._id, camp.name);
                                        }}
                                        disabled={isDownloading}
                                        title="Download Delivery Report CSV"
                                      >
                                        <i className="fas fa-download me-1"></i> CSV
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        className="fw-bold px-2 py-1 text-white border-0" 
                                        style={{ backgroundColor: "#28a745", borderRadius: "4px" }}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleDownloadReplies(camp._id, camp.name);
                                        }}
                                        disabled={downloadingRepliesId === camp._id}
                                        title="Export Inbound Replies CSV"
                                      >
                                        {downloadingRepliesId === camp._id ? (
                                          <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Replies</>
                                        ) : (
                                          <><i className="fas fa-reply me-1"></i> Replies</>
                                        )}
                                      </Button>
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      ) : (
        <Card className="shadow-sm border-0 text-center py-5">
           <h5 className="text-muted fw-bold">No campaigns yet.</h5>
           <p className="text-muted mb-0">Click "Create Campaign" to launch your first blast.</p>
        </Card>
      )}
    </Container>
  );
}

export default Campaigns;